/**
 * Drilldowns Tab Component
 * Phase 9.38 - Dashboard Stat Drilldowns
 *
 * Container for all drilldown reports with sub-navigation
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AvailableToInvoiceByProject,
  OutstandingByProject,
  YTDRevenueByProject,
  OverdueAging,
  PipelineBreakdown,
  MetricsByClient,
  CashFlowByWeek,
} from './drilldowns';

export type DrilldownView =
  | 'available-to-invoice'
  | 'outstanding'
  | 'ytd-revenue'
  | 'overdue-aging'
  | 'pipeline-breakdown'
  | 'metrics-by-client'
  | 'cash-flow-by-week';

interface DrilldownNavItem {
  id: DrilldownView;
  label: string;
  section: 'project' | 'client' | 'aging' | 'pipeline' | 'cash-flow';
}

const drilldownItems: DrilldownNavItem[] = [
  { id: 'available-to-invoice', label: 'Available to Invoice', section: 'project' },
  { id: 'outstanding', label: 'Outstanding', section: 'project' },
  { id: 'ytd-revenue', label: 'YTD Revenue', section: 'project' },
  { id: 'metrics-by-client', label: 'Metrics by Client', section: 'client' },
  { id: 'overdue-aging', label: 'Overdue Aging', section: 'aging' },
  { id: 'pipeline-breakdown', label: 'Pipeline Breakdown', section: 'pipeline' },
  { id: 'cash-flow-by-week', label: 'Cash Flow by Week', section: 'cash-flow' },
];

export function DrilldownsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view') as DrilldownView | null;
  const [activeView, setActiveView] = useState<DrilldownView>(
    viewParam && drilldownItems.some(item => item.id === viewParam)
      ? viewParam
      : 'available-to-invoice'
  );

  const handleViewChange = (view: DrilldownView) => {
    setActiveView(view);
    setSearchParams({ tab: 'drilldowns', view });
  };

  const renderDrilldown = () => {
    switch (activeView) {
      case 'available-to-invoice':
        return <AvailableToInvoiceByProject />;
      case 'outstanding':
        return <OutstandingByProject />;
      case 'ytd-revenue':
        return <YTDRevenueByProject />;
      case 'overdue-aging':
        return <OverdueAging />;
      case 'pipeline-breakdown':
        return <PipelineBreakdown />;
      case 'metrics-by-client':
        return <MetricsByClient />;
      case 'cash-flow-by-week':
        return <CashFlowByWeek />;
      default:
        return null;
    }
  };

  // Group items by section for display
  const projectItems = drilldownItems.filter(item => item.section === 'project');
  const clientItems = drilldownItems.filter(item => item.section === 'client');
  const agingItems = drilldownItems.filter(item => item.section === 'aging');
  const pipelineItems = drilldownItems.filter(item => item.section === 'pipeline');
  const cashFlowItems = drilldownItems.filter(item => item.section === 'cash-flow');

  const renderNavButton = (item: DrilldownNavItem) => (
    <button
      key={item.id}
      data-testid={`drilldown-${item.id}`}
      onClick={() => handleViewChange(item.id)}
      className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
        activeView === item.id
          ? 'bg-blue-600 text-white'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {item.label}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="drilldowns-tab">
      {/* Sub-navigation */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex flex-wrap gap-6">
          {/* By Project */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              By Project
            </div>
            <div className="flex gap-2">
              {projectItems.map(renderNavButton)}
            </div>
          </div>

          {/* By Client */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              By Client
            </div>
            <div className="flex gap-2">
              {clientItems.map(renderNavButton)}
            </div>
          </div>

          {/* Aging */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Aging
            </div>
            <div className="flex gap-2">
              {agingItems.map(renderNavButton)}
            </div>
          </div>

          {/* Pipeline */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Pipeline
            </div>
            <div className="flex gap-2">
              {pipelineItems.map(renderNavButton)}
            </div>
          </div>

          {/* Cash Flow */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Cash Flow
            </div>
            <div className="flex gap-2">
              {cashFlowItems.map(renderNavButton)}
            </div>
          </div>
        </div>
      </div>

      {/* Active Drilldown Content */}
      <div data-testid={`drilldown-content-${activeView}`}>
        {renderDrilldown()}
      </div>
    </div>
  );
}
